import { Injectable, inject } from '@angular/core';
import { LegalDocument } from '../models/legal-document.model';
import { LanguageCode } from '../models/language.model';
import { Firestore, addDoc, and, collection, doc, getDocs, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

@Injectable({
  providedIn: 'root',
})
export class LegalDocumentService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private collectionName = 'legalDocuments';

  async getAllDocuments(
    documentType: 'agb' | 'datenschutz'
  ): Promise<LegalDocument[]> {
    try {
      const legalDocsRef = collection(this.firestore, this.collectionName);
      const q = query(
        legalDocsRef,
        where('documentType', '==', documentType),
        orderBy('version', 'desc'),
        orderBy('language')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as LegalDocument[];
    } catch (error) {
      console.error('Error fetching legal documents:', error);
      throw error;
    }
  }

  async getDocumentsByVersion(
    documentType: 'agb' | 'datenschutz',
    version: string
  ): Promise<LegalDocument[]> {
    try {
      const legalDocsRef = collection(this.firestore, this.collectionName);
      const q = query(
        legalDocsRef,
        and(
          where('documentType', '==', documentType),
          where('version', '==', version)
        )
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as LegalDocument[];
    } catch (error) {
      console.error('Error fetching documents by version:', error);
      throw error;
    }
  }

  async getActiveDocument(
    documentType: 'agb' | 'datenschutz',
    language: LanguageCode = 'de'
  ): Promise<LegalDocument | null> {
    try {
      // First, try to get document in the requested language
      const legalDocsRef = collection(this.firestore, this.collectionName);
      const q = query(
        legalDocsRef,
        and(
          where('documentType', '==', documentType),
          where('isActive', '==', true),
          where('language', '==', language)
        )
      );

      const querySnapshot = await getDocs(q);

      // If document exists in requested language, return it
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as LegalDocument;
      }
      console.log('No document found in requested language:', language);
      // If requested language is already German, no need for fallback
      if (language === 'de') {
        return null;
      }

      // Fallback to German language
      const fallbackQuery = query(
        legalDocsRef,
        and(
          where('documentType', '==', documentType),
          where('isActive', '==', true),
          where('language', '==', 'de')
        )
      );

      const fallbackSnapshot = await getDocs(fallbackQuery);

      if (!fallbackSnapshot.empty) {
        const doc = fallbackSnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        } as LegalDocument;
      }

      // No document found in requested language or German
      return null;
    } catch (error) {
      console.error('Error getting active document:', error);
      throw error;
    }
  }

  async createDocument(data: Partial<LegalDocument>): Promise<string> {
    try {
      const createdOn = new Date();

      // Check if a document with same version/language/documentType already exists
      const existingDoc = await this.findExistingDocument(
        data.documentType as 'agb' | 'datenschutz',
        data.version as string,
        data.language as LanguageCode
      );

      if (existingDoc) {
        throw new Error(
          `Ein Dokument mit Version "${data.version}" und Sprache "${data.language}" existiert bereits.`
        );
      }

      // If this document is set to active, deactivate all other documents of same type and language
      if (data.isActive) {
        await this.deactivateOtherDocuments(
          data.documentType as 'agb' | 'datenschutz',
          data.language as LanguageCode
        );
      }

      const docRef = await addDoc(
        collection(this.firestore, this.collectionName),
        {
          ...data,
          createdOn,
        }
      );

      return docRef.id;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  async updateDocument(
    id: string,
    data: Partial<LegalDocument>
  ): Promise<void> {
    try {
      // If this document is set to active, deactivate all other documents of same type and language
      if (data.isActive) {
        await this.deactivateOtherDocuments(
          data.documentType as 'agb' | 'datenschutz',
          data.language as LanguageCode,
          id
        );
      }

      const updatedOn = new Date();
      const docRef = doc(this.firestore, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedOn,
      });
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  private async findExistingDocument(
    documentType: 'agb' | 'datenschutz',
    version: string,
    language: LanguageCode
  ): Promise<LegalDocument | null> {
    const legalDocsRef = collection(this.firestore, this.collectionName);
    const q = query(
      legalDocsRef,
      and(
        where('documentType', '==', documentType),
        where('version', '==', version),
        where('language', '==', language)
      )
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data(),
    } as LegalDocument;
  }

  private async deactivateOtherDocuments(
    documentType: 'agb' | 'datenschutz',
    language: LanguageCode,
    exceptId?: string
  ): Promise<void> {
    try {
      const legalDocsRef = collection(this.firestore, this.collectionName);
      const q = query(
        legalDocsRef,
        and(
          where('documentType', '==', documentType),
          where('language', '==', language),
          where('isActive', '==', true)
        )
      );

      const querySnapshot = await getDocs(q);

      const updatePromises = querySnapshot.docs
        .filter((d) => !exceptId || d.id !== exceptId)
        .map((d) => {
          const docRef = doc(this.firestore, this.collectionName, d.id);
          return updateDoc(docRef, { isActive: false });
        });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error deactivating other documents:', error);
      throw error;
    }
  }
}
